import { schema } from 'normalizr';
import { AxiosResponse } from 'axios';

import { ActivityType, ActivityKey, MinimalActivity } from 'redux/schemas/models/activity';
import {
  Communication,
  CommunicationFilter,
  CommunicationType,
  FilterType,
  OwnerType,
  Recipient,
  ScheduledForType,
  ScheduledTimeLineLoadingType,
} from 'redux/schemas/models/course-communication';
import { TriggerType } from 'course_communications/contexts/communication-context';
import { MinimalExercise, TimelineExercisesNormalized } from 'redux/schemas/models/exercise';
import { MinimalQuiz, TimelineQuizzesNormalized } from 'redux/schemas/models/quiz';
import { MinimalTimedQuiz, TimelineTimedQuizzesNormalized } from 'redux/schemas/models/timed-quiz';
import { MinimalSurvey, TimelineSurveysNormalized } from 'redux/schemas/models/survey';
import { MinimalLectureVideo, TimelineLectureVideosNormalized } from 'redux/schemas/models/lecture-video';
import { MinimalPost, TimelinePostsNormalized } from 'redux/schemas/models/timeline-post';
import { MinimalExternalTool, TimelineExternalToolsNormalized } from 'redux/schemas/models/external-tool';
import { MinimalTeamSet, TimelineTeamSetsNormalized } from 'redux/schemas/models/team-set';
import { MinimalGroupTeamSet, TimelineGroupTeamSetsNormalized } from 'redux/schemas/models/group-team-set';
import { MinimalLiveSession, TimelineLiveSessionsNormalized } from 'redux/schemas/models/live-session';
import { MinimalPeerEvaluation, TimelinePeerEvaluationsNormalized } from 'redux/schemas/models/peer-evaluation';
import { LecturePage, LecturePageKey, LecturePagesNormalized, MinimalLecturePage } from 'redux/schemas/models/lecture-page';
import { MinimalProfileRequirement, ProfileRequirementsNormalized } from 'redux/schemas/models/profile-requirement';
import { MinimalTeamDiscussion, TeamDiscussionsNormalized } from '../models/team-discussion';
import { MinimalPoll, PollsNormalizedDictionary } from '../models/poll';
import { VideoPracticeActivitiesNormalized, MinimalVideoPracticeActivity } from '../models/video-practice';
import { MinimalVideoPracticeFeedbackActivity, VideoPracticeFeedbackActivitiesNormalized } from '../models/video-practice-feedback';

const CommunicationSchema = new schema.Entity<Communication>('communications');
export const CommunicationListSchema = new schema.Array(CommunicationSchema);

export type FetchCourseActivitiesParams = {
  catalogId: string,
};

export interface CourseActivitiesResponse {
  [ActivityKey.EXERCISES]: MinimalExercise[]
  [ActivityKey.QUIZZES]: MinimalQuiz[]
  [ActivityKey.TIMED_QUIZZES]: MinimalTimedQuiz[]
  [ActivityKey.SURVEYS]: MinimalSurvey[]
  [ActivityKey.TEAM_SET]: MinimalTeamSet[]
  [ActivityKey.GROUP_TEAM_SET]: MinimalGroupTeamSet[]
  [ActivityKey.TOPICS]: MinimalPost[]
  [ActivityKey.CUSTOM_QUESTIONS]: MinimalPeerEvaluation[]
  [ActivityKey.EXTERNAL_TOOLS]: MinimalExternalTool[]
  [ActivityKey.LIVE_SESSION]: MinimalLiveSession[]
  [ActivityKey.LECTURE_VIDEOS]: MinimalLectureVideo[]
  [ActivityKey.PROFILE_REQUIREMENT]: MinimalProfileRequirement[]
  [ActivityKey.POLLS]: MinimalPoll[]
  [ActivityKey.VIDEO_PRACTICES]: MinimalVideoPracticeActivity[]
  [ActivityKey.VIDEO_PRACTICE_FEEDBACK]: MinimalVideoPracticeFeedbackActivity[]
  totalPoints: number
}

export type BaseCourseActivities = {
  [key in keyof typeof ActivityKey]?: MinimalActivity
};

export interface CourseActivities extends BaseCourseActivities {
  [ActivityKey.EXERCISES]?: MinimalExercise[]
  [ActivityKey.QUIZZES]?: MinimalQuiz[]
  [ActivityKey.TIMED_QUIZZES]?: MinimalTimedQuiz[]
  [ActivityKey.SURVEYS]?: MinimalSurvey[]
  [ActivityKey.TEAM_SET]?: MinimalTeamSet[]
  [ActivityKey.GROUP_TEAM_SET]?: MinimalGroupTeamSet[]
  [ActivityKey.TOPICS]?: MinimalPost[]
  [ActivityKey.TEAM_DISCUSSION]?: MinimalTeamDiscussion[]
  [ActivityKey.CUSTOM_QUESTIONS]?: MinimalPeerEvaluation[]
  [ActivityKey.EXTERNAL_TOOLS]?: MinimalExternalTool[]
  [ActivityKey.LIVE_SESSION]?: MinimalLiveSession[]
  [ActivityKey.LECTURE_VIDEOS]?: MinimalLectureVideo[]
  [ActivityKey.PROFILE_REQUIREMENT]?: MinimalProfileRequirement[]
  [ActivityKey.POLLS]?: MinimalPoll[]
  [ActivityKey.VIDEO_PRACTICES]?: MinimalVideoPracticeActivity[]
  [ActivityKey.VIDEO_PRACTICE_FEEDBACK]?: MinimalVideoPracticeFeedbackActivity[]
}

export const CourseActivitiesSchema = new schema.Object({
  [ActivityKey.EXERCISES]: new schema.Array(new schema.Entity<MinimalExercise>('exercises', {})),
  [ActivityKey.QUIZZES]: new schema.Array(new schema.Entity<MinimalQuiz>('quizzes', {})),
  [ActivityKey.TIMED_QUIZZES]: new schema.Array(new schema.Entity<MinimalTimedQuiz>('timedQuizzes', {})),
  [ActivityKey.SURVEYS]: new schema.Array(new schema.Entity<MinimalSurvey>('surveys', {})),
  [ActivityKey.TEAM_SET]: new schema.Array(new schema.Entity<MinimalTeamSet>('teamSets', {})),
  [ActivityKey.GROUP_TEAM_SET]: new schema.Array(new schema.Entity<MinimalGroupTeamSet>('groupTeamSets', {})),
  [ActivityKey.TOPICS]: new schema.Array(new schema.Entity<MinimalPost>('posts', {})),
  [ActivityKey.TEAM_DISCUSSION]: new schema.Array(new schema.Entity<MinimalTeamDiscussion>('teamDiscussions', {})),
  [ActivityKey.CUSTOM_QUESTIONS]: new schema.Array(new schema.Entity<MinimalPeerEvaluation>('peerEvaluations', {})),
  [ActivityKey.EXTERNAL_TOOLS]: new schema.Array(new schema.Entity<MinimalExternalTool>('externalTools', {})),
  [ActivityKey.LIVE_SESSION]: new schema.Array(new schema.Entity<MinimalLiveSession>('liveSessions', {})),
  [ActivityKey.LECTURE_VIDEOS]: new schema.Array(new schema.Entity<MinimalLectureVideo>('lectureVideos', {})),
  [ActivityKey.PROFILE_REQUIREMENT]: new schema.Array(new schema.Entity<MinimalProfileRequirement>('profileRequirements', {})),
  [ActivityKey.POLLS]: new schema.Array(new schema.Entity<MinimalPoll>('polls', {})),
  [ActivityKey.VIDEO_PRACTICES]: new schema.Array(new schema.Entity<MinimalVideoPracticeActivity>('practiceActivities', {})),
  [ActivityKey.VIDEO_PRACTICE_FEEDBACK]: new schema.Array(new schema.Entity<MinimalVideoPracticeFeedbackActivity>('practiceFeedbackActivities', {})),
});

export interface CourseActivitiesEntities {
  [ActivityKey.EXERCISES]?: TimelineExercisesNormalized
  [ActivityKey.QUIZZES]?: TimelineQuizzesNormalized
  [ActivityKey.TIMED_QUIZZES]?: TimelineTimedQuizzesNormalized
  [ActivityKey.SURVEYS]?: TimelineSurveysNormalized
  [ActivityKey.TEAM_SET]?: TimelineTeamSetsNormalized
  [ActivityKey.GROUP_TEAM_SET]?: TimelineGroupTeamSetsNormalized
  [ActivityKey.TOPICS]?: TimelinePostsNormalized
  [ActivityKey.TEAM_DISCUSSION]?: TeamDiscussionsNormalized
  [ActivityKey.CUSTOM_QUESTIONS]?: TimelinePeerEvaluationsNormalized
  [ActivityKey.EXTERNAL_TOOLS]?: TimelineExternalToolsNormalized
  [ActivityKey.LIVE_SESSION]?: TimelineLiveSessionsNormalized
  [ActivityKey.LECTURE_VIDEOS]?: TimelineLectureVideosNormalized
  [ActivityKey.PROFILE_REQUIREMENT]?: ProfileRequirementsNormalized
  [ActivityKey.POLLS]?: PollsNormalizedDictionary
  [ActivityKey.VIDEO_PRACTICES]?: VideoPracticeActivitiesNormalized
  [ActivityKey.VIDEO_PRACTICE_FEEDBACK]?: VideoPracticeFeedbackActivitiesNormalized
}

export interface CourseActivitiesNormalizedResult {
  [ActivityKey.EXERCISES]: number[]
  [ActivityKey.QUIZZES]: number[]
  [ActivityKey.TIMED_QUIZZES]: number[]
  [ActivityKey.SURVEYS]: number[]
  [ActivityKey.TEAM_SET]: number[]
  [ActivityKey.GROUP_TEAM_SET]: number[]
  [ActivityKey.TOPICS]: number[]
  [ActivityKey.TEAM_DISCUSSION]: number[]
  [ActivityKey.CUSTOM_QUESTIONS]: number[]
  [ActivityKey.EXTERNAL_TOOLS]: number[]
  [ActivityKey.LIVE_SESSION]: number[]
  [ActivityKey.LECTURE_VIDEOS]: number[]
  [ActivityKey.PROFILE_REQUIREMENT]: number[]
  [ActivityKey.POLLS]: number[]
  [ActivityKey.VIDEO_PRACTICES]: number[]
  [ActivityKey.VIDEO_PRACTICE_FEEDBACK]: number[]
}

export type FetchLecturePageCommunicationsParams = {
  catalogId: string,
  lecturePageId: number,
  showCommunications: boolean | undefined,
};

export interface LecturePageCommunicationsResponse {
  lecturePages: LecturePage
}

interface SendEmailParams {
  subject: string
  body: string
}

export interface BaseCommunicationParams {
  catalogId: string
  communicationType: CommunicationType
  submitted: boolean
  ownerId: number
  ownerType: OwnerType
  scheduledFor?: ScheduledForType
  xDays?: number
  recipients: Recipient[]
  communication: SendEmailParams
  enablePushNotification: boolean
  pushNotificationBody?: string
  filters?: CommunicationFilter
  callback?: (res: any) => void
  event?: CommunicationFilter
  lecturePageId?: number
  triggerType?: TriggerType
}

export interface UpdateCommunicationParams extends BaseCommunicationParams {
  communicationId: number
}

export interface CreateCommunicationParams extends BaseCommunicationParams {}

export type CommunicationParams = UpdateCommunicationParams | CreateCommunicationParams;

export type DeleteCommunicationParams = {
  catalogId: string,
  communicationId: number,
  lecturePageId: number,
};

export interface DeleteCommunicationResponse {
  result: boolean,
}

export interface GetEmailPreviewParams {
  catalogId: string
  email: {
    subject: string
    body: string
  }
  callback(requestPromise: Promise<AxiosResponse<any>>): void,
}

export type FetchDraftCommunicationsParams = {
  catalogId: string,
};

export type FetchScheduledDueDatesParams = {
  catalogId: string,
};

export interface ScheduledDueDatesResponse {
  [LecturePageKey.LECTURE]: MinimalLecturePage[]
  [ActivityKey.EXERCISES]: MinimalExercise[]
  [ActivityKey.QUIZZES]: MinimalQuiz[]
  [ActivityKey.TIMED_QUIZZES]: MinimalTimedQuiz[]
  [ActivityKey.SURVEYS]: MinimalSurvey[]
  [ActivityKey.TEAM_SET]: MinimalTeamSet[]
  [ActivityKey.GROUP_TEAM_SET]: MinimalGroupTeamSet[]
  [ActivityKey.TOPICS]: MinimalPost[]
  [ActivityKey.CUSTOM_QUESTIONS]: MinimalPeerEvaluation[]
  [ActivityKey.EXTERNAL_TOOLS]: MinimalExternalTool[]
  [ActivityKey.LIVE_SESSION]: MinimalLiveSession[]
  [ActivityKey.LECTURE_VIDEOS]: MinimalLectureVideo[]
  [ActivityKey.POLLS]: MinimalPoll[]
}

export const ScheduledDueDatesSchema = new schema.Object({
  [LecturePageKey.LECTURE]: new schema.Array(new schema.Entity<MinimalLecturePage>('lecturePages', {})),
  [ActivityKey.EXERCISES]: new schema.Array(new schema.Entity<MinimalExercise>('exercises', {})),
  [ActivityKey.QUIZZES]: new schema.Array(new schema.Entity<MinimalQuiz>('quizzes', {})),
  [ActivityKey.TIMED_QUIZZES]: new schema.Array(new schema.Entity<MinimalTimedQuiz>('timedQuizzes', {})),
  [ActivityKey.SURVEYS]: new schema.Array(new schema.Entity<MinimalSurvey>('surveys', {})),
  [ActivityKey.TEAM_SET]: new schema.Array(new schema.Entity<MinimalTeamSet>('teamSets', {})),
  [ActivityKey.GROUP_TEAM_SET]: new schema.Array(new schema.Entity<MinimalGroupTeamSet>('groupTeamSets', {})),
  [ActivityKey.TOPICS]: new schema.Array(new schema.Entity<MinimalPost>('posts', {})),
  [ActivityKey.CUSTOM_QUESTIONS]: new schema.Array(new schema.Entity<MinimalPeerEvaluation>('peerEvaluations', {})),
  [ActivityKey.EXTERNAL_TOOLS]: new schema.Array(new schema.Entity<MinimalExternalTool>('externalTools', {})),
  [ActivityKey.LIVE_SESSION]: new schema.Array(new schema.Entity<MinimalLiveSession>('liveSessions', {})),
  [ActivityKey.LECTURE_VIDEOS]: new schema.Array(new schema.Entity<MinimalLectureVideo>('lectureVideos', {})),
  [ActivityKey.POLLS]: new schema.Array(new schema.Entity<MinimalPoll>('polls', {})),
});

export interface ScheduledDueDatesEntities {
  [LecturePageKey.LECTURE]?: LecturePagesNormalized
  [ActivityKey.EXERCISES]?: TimelineExercisesNormalized
  [ActivityKey.QUIZZES]?: TimelineQuizzesNormalized
  [ActivityKey.TIMED_QUIZZES]?: TimelineTimedQuizzesNormalized
  [ActivityKey.SURVEYS]?: TimelineSurveysNormalized
  [ActivityKey.TEAM_SET]?: TimelineTeamSetsNormalized
  [ActivityKey.GROUP_TEAM_SET]?: TimelineGroupTeamSetsNormalized
  [ActivityKey.TOPICS]?: TimelinePostsNormalized
  [ActivityKey.CUSTOM_QUESTIONS]?: TimelinePeerEvaluationsNormalized
  [ActivityKey.EXTERNAL_TOOLS]?: TimelineExternalToolsNormalized
  [ActivityKey.LIVE_SESSION]?: TimelineLiveSessionsNormalized
  [ActivityKey.LECTURE_VIDEOS]?: TimelineLectureVideosNormalized
  [ActivityKey.POLLS]?: PollsNormalizedDictionary
}

export interface ScheduledDueDatesNormalizedResult {
  [LecturePageKey.LECTURE]: number[]
  [ActivityKey.EXERCISES]: number[]
  [ActivityKey.QUIZZES]: number[]
  [ActivityKey.TIMED_QUIZZES]: number[]
  [ActivityKey.SURVEYS]: number[]
  [ActivityKey.TEAM_SET]: number[]
  [ActivityKey.GROUP_TEAM_SET]: number[]
  [ActivityKey.TOPICS]: number[]
  [ActivityKey.CUSTOM_QUESTIONS]: number[]
  [ActivityKey.EXTERNAL_TOOLS]: number[]
  [ActivityKey.LIVE_SESSION]: number[]
  [ActivityKey.LECTURE_VIDEOS]: number[]
  [ActivityKey.POLLS]: number[]
}

export type FetchScheduledCommunicationsParams = {
  catalogId: string,
  startTime: string,
  display: ScheduledTimeLineLoadingType,
  page: number,
  showCommunications: boolean,
};

export interface ScheduledCommunicationsResponse {
  communications: Communication[],
  count: number,
  perPage: number,
}

export type FetchFilteredCommunicationsParams = {
  catalogId: string,
  filterBy: FilterType,
  page: number,
};

export interface FilteredCommunicationsResponse {
  communications: Communication[],
  count: number,
  perPage: number,
}

export type HidePastScheduledCommunicationsParams = {
  startTime: string,
};
